import { useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import AddressAutocomplete from './AddressAutocomplete';

const FONT = 'Manrope, system-ui, sans-serif';

/**
 * Mappa semplificata per scegliere la zona di ricerca (B2C): solo la mappa +
 * ricerca indirizzo + disegno freehand dell'area + "Conferma zona". Niente
 * filtri/tab bar: serve solo a definire DOVE cercare. Marina 2026-07-12.
 *
 * onConfirm(zone):
 *   - area disegnata → { area: [{lat,lng}...], center_label }
 *   - altrimenti     → { center_lat, center_lng, center_label }
 */
export default function ZonePickerMap({ onConfirm, onClose, initialCenter, drawFirst = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const gRef = useRef(null);
  const projRef = useRef(null);
  const polyRef = useRef(null);
  const drawnPathRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');
  const [label, setLabel] = useState('');
  const [drawArm, setDrawArm] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [hasArea, setHasArea] = useState(false);

  // Init mappa
  useEffect(() => {
    let cancelled = false;
    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    // Il loader è un singleton condiviso con la mappa principale: configura solo
    // se Google non è già stato caricato, per evitare doppie setOptions.
    if (!(window.google && window.google.maps)) {
      try { setOptions({ key, v: 'weekly' }); } catch (_) {}
    }
    Promise.all([importLibrary('core'), importLibrary('maps')]).then(([, maps]) => {
      if (cancelled || !containerRef.current) return;
      const google = window.google;
      gRef.current = google;
      const center = initialCenter || { lat: 41.9028, lng: 12.4964 };
      const map = new google.maps.Map(containerRef.current, {
        center, zoom: initialCenter ? 12 : 6,
        disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', clickableIcons: false,
      });
      mapRef.current = map;
      // OverlayView per la proiezione pixel→latlng (disegno freehand)
      const ov = new google.maps.OverlayView();
      ov.onAdd = () => {}; ov.draw = () => {}; ov.onRemove = () => {};
      ov.setMap(map);
      projRef.current = ov;
      setReady(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Centra la mappa su un indirizzo scelto
  const onPickAddress = ({ lat, lng, label: lbl, viewport }) => {
    setLabel(lbl || ''); setZoneSearch(lbl || '');
    clearArea();
    const g = gRef.current, map = mapRef.current;
    if (!g || !map) return;
    if (viewport && viewport.south != null) {
      map.fitBounds(new g.maps.LatLngBounds({ lat: viewport.south, lng: viewport.west }, { lat: viewport.north, lng: viewport.east }), 20);
    } else {
      map.setCenter({ lat, lng }); map.setZoom(13);
    }
  };

  const clearArea = () => {
    if (polyRef.current) { polyRef.current.setMap(null); polyRef.current = null; }
    drawnPathRef.current = null; setHasArea(false);
  };

  const commitArea = (path) => {
    const g = gRef.current, map = mapRef.current;
    if (!g || !map || !path || path.length < 3) return;
    clearArea();
    polyRef.current = new g.maps.Polygon({
      paths: path, map, strokeColor: '#1F6FEB', strokeOpacity: 0.95, strokeWeight: 2.5,
      fillColor: '#1F6FEB', fillOpacity: 0.08, clickable: false, zIndex: 5,
    });
    drawnPathRef.current = path;
    setHasArea(true);
    // fit
    let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
    path.forEach((p) => { w = Math.min(w, p.lng); e = Math.max(e, p.lng); s = Math.min(s, p.lat); n = Math.max(n, p.lat); });
    map.fitBounds(new g.maps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), 40);
    setLabel('Area disegnata');
  };

  // Disegno freehand
  useEffect(() => {
    if (!drawMode || !ready || !mapRef.current || !containerRef.current) return;
    const g = gRef.current, map = mapRef.current, el = containerRef.current, ov = projRef.current;
    const prev = { gh: map.get('gestureHandling'), drag: map.get('draggable') };
    map.setOptions({ gestureHandling: 'none', draggable: false, disableDoubleClickZoom: true });
    el.style.cursor = 'crosshair';
    let drawing = false, pts = [], line = null;
    const toLatLng = (cx, cy) => {
      const proj = ov && ov.getProjection && ov.getProjection();
      if (!proj) return null;
      const r = el.getBoundingClientRect();
      const ll = proj.fromContainerPixelToLatLng(new g.maps.Point(cx - r.left, cy - r.top));
      return ll ? { lat: ll.lat(), lng: ll.lng() } : null;
    };
    const start = (x, y) => {
      const ll = toLatLng(x, y); if (!ll) return;
      drawing = true; pts = [ll];
      line = new g.maps.Polyline({ map, path: [ll], strokeColor: '#1F6FEB', strokeWeight: 3, strokeOpacity: 0.95, zIndex: 6 });
    };
    const move = (x, y) => {
      if (!drawing) return;
      const ll = toLatLng(x, y); if (!ll) return;
      pts.push(ll);
      if (line) line.getPath().push(new g.maps.LatLng(ll.lat, ll.lng));
    };
    const end = () => {
      if (!drawing) return;
      drawing = false;
      if (line) { line.setMap(null); line = null; }
      if (pts.length >= 3) commitArea(pts);
      setDrawMode(false);
    };
    const onDown = (ev) => { const t = ev.touches ? ev.touches[0] : ev; ev.preventDefault(); start(t.clientX, t.clientY); };
    const onMove = (ev) => { if (!drawing) return; ev.preventDefault(); const t = ev.touches ? ev.touches[0] : ev; move(t.clientX, t.clientY); };
    const onUp = () => end();
    el.addEventListener('mousedown', onDown);
    el.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('touchstart', onDown, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('touchstart', onDown);
      el.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      el.style.cursor = '';
      if (line) line.setMap(null);
      map.setOptions({ gestureHandling: prev.gh || 'greedy', draggable: prev.drag !== false, disableDoubleClickZoom: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawMode, ready]);

  const confirm = () => {
    const g = gRef.current, map = mapRef.current;
    if (drawnPathRef.current && drawnPathRef.current.length >= 3) {
      onConfirm({ area: drawnPathRef.current, center_label: label || 'Area disegnata' });
      return;
    }
    if (map) {
      const c = map.getCenter();
      onConfirm({ center_lat: c.lat(), center_lng: c.lng(), center_label: label || 'Zona selezionata' });
    } else {
      onClose();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5200, background: '#fff', display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
      {/* mappa */}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {/* top: back + ricerca */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3,
        padding: 'calc(10px + env(safe-area-inset-top, 0px)) 12px 10px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <button onClick={onClose} aria-label="Indietro" style={{
          flex: '0 0 auto', border: 0, background: '#fff', width: 42, height: 42, borderRadius: 12,
          boxShadow: '0 2px 10px rgba(0,0,0,0.15)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1F2937" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="zpm-search" style={{ flex: 1, background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>
          <AddressAutocomplete
            value={zoneSearch}
            onChange={setZoneSearch}
            onSelect={onPickAddress}
            placeholder="Cerca città, indirizzo o regione"
            inputClassName="zpm-input"
          />
        </div>
      </div>

      {/* banner disegno attivo */}
      {drawMode && (
        <div style={{
          position: 'absolute', left: 12, right: 12, top: 'calc(72px + env(safe-area-inset-top, 0px))', zIndex: 4,
          background: '#1F2937', color: '#fff', borderRadius: 12, padding: '10px 14px', textAlign: 'center',
          fontSize: 13.5, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        }}>Tieni premuto e trascina per disegnare il perimetro</div>
      )}

      {/* pulsanti bottom */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4,
        padding: '14px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', flexDirection: 'column', gap: 10,
        background: 'linear-gradient(to top, rgba(255,255,255,0.96), rgba(255,255,255,0))',
      }}>
        {!drawMode && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            {!hasArea ? (
              <button onClick={() => setDrawMode(true)} style={pillBtn('#fff', '#1F6FEB', '1.5px solid #C9D6EE')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1F6FEB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><circle cx="11" cy="11" r="2" /></svg>
                Disegna un'area
              </button>
            ) : (
              <button onClick={clearArea} style={pillBtn('#fff', '#C2453B', '1.5px solid #F3C9C4')}>
                ✕ Rimuovi area
              </button>
            )}
          </div>
        )}
        {/* Nel flusso "disegna un'area" (drawFirst) il tasto Conferma appare SOLO
            dopo aver effettivamente disegnato l'area. Marina 2026-07-14. */}
        {!drawMode && (!drawFirst || hasArea) && (
          <button onClick={confirm} style={{
            width: '100%', border: 0, background: '#1F6FEB', color: '#fff', borderRadius: 14,
            padding: '15px', fontWeight: 900, fontSize: 15.5, cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(31,111,235,0.4)',
          }}>Conferma zona</button>
        )}
      </div>
    </div>
  );
}

const pillBtn = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, background: bg, color, border,
  borderRadius: 999, padding: '11px 18px', fontWeight: 800, fontSize: 14, cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
});
